import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import parse from "html-react-parser";
import UnstyledImage from "./image";
import { Link } from "gatsby";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLongArrowAltRight } from "@fortawesome/free-solid-svg-icons";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0.6rem 0;
  flex: 1;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    flex: auto;
  }
`;
const SmWrapper = styled(Wrapper)`
  border-radius: 5px;
  width: auto;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    width: 48%;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: ${({ $twoCol }) => ($twoCol ? "50%" : "100%")};
    margin: ${({ $twoCol }) => ($twoCol ? "0" : "1rem 0")};
  }
`;
const LinkImage = styled(Link)`
  height: ${({ $isSmall }) => ($isSmall ? "150px" : "400px")};
  border-radius: ${({ $isSmall }) =>
    $isSmall ? "5px" : "var(--border-radius)"};
  overflow: hidden;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    height: 350px;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    height: ${({ $twoCol }) => ($twoCol ? "178px" : "315px")};
  }
`;
const ImageWrapper = styled.div`
  height: ${({ $isSmall }) => ($isSmall ? "150px" : "400px")};
  border-radius: ${({ $isSmall }) =>
    $isSmall ? "5px" : "var(--border-radius)"};
  overflow: hidden;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    height: 350px;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    height: ${({ $twoCol }) => ($twoCol ? "178px" : "315px")};
  }
`;
const LinkDiv = styled(Link)`
  padding: ${({ $isSmall }) => ($isSmall ? "1rem 0 0 0" : "1rem")};
  position: relative;
  inset: 0;
  text-decoration: none;
  text-align: ${({ $textAlign }) =>
    $textAlign === "right"
      ? "right"
      : $textAlign === "center"
        ? "center"
        : "left"};
`;
const TextDiv = styled.span`
  padding: ${({ $isSmall }) => ($isSmall ? "1rem 0 0 0" : "1rem")};
  position: relative;
  inset: 0;
  text-decoration: none;
  text-align: ${({ $textAlign }) =>
    $textAlign === "right"
      ? "right"
      : $textAlign === "center"
        ? "center"
        : "left"};
`;
const Image = styled(UnstyledImage)`
  max-width: 100%;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease-in-out !important;
  @media (hover: hover) {
    &:hover {
      transform: ${({ $disableHover }) =>
        $disableHover ? "none" : "scale(1.04)"};
      cursor: ${({ $disableHover }) => ($disableHover ? "auto" : "pointer")};
      @media (prefers-reduced-motion) {
        transform: none;
      }
    }
  }
`;
const Title = styled.p`
  padding: 0;
  font-family: ${({ theme }) => theme.fontLinks.fontFamily};
  font-weight: ${({ $isSmall }) => ($isSmall ? "600" : "800")};
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.darkGray};
  margin: 0.8rem 0 0 0;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding: 1rem 0 0 0;
    font-size: ${({ $twoCol }) => ($twoCol ? "0.9rem" : "1.1rem")};
    font-weight: ${({ $twoCol }) => ($twoCol ? "600" : "300")};
  }
`;
const Description = styled.div`
  padding: 0.5rem 0;
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
  font-size: 0.9rem;
  line-height: 1.4;
  font-weight: 400;
  margin-top: 0;
  color: var(--color-black);
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding: 0.5rem 0;
    margin: 0;
    font-size: ${({ $twoCol }) => ($twoCol ? "0.8rem" : "0.9rem")};
  }
  & p {
    margin: 0;
    font-size: 0.9rem;
  }
`;
const AbsoluteArrow = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.primary};
  position: absolute;
  right: 1.5rem;
`;
const ArrowIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.primary};
  margin-left: 0.5rem;
  transition: all 0.3s ease;
`;
const CardLink = styled(Link)`
  padding: 0;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.darkGray};
  font-family: ${({ theme }) => theme.fontLinks.fontFamily};
  font-size: 15px;
  width: fit-content;
  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
      svg {
        margin-left: 0.75rem;
      }
    }
  }
  span {
    margin-top: 5px;
    transition: all 0.3s ease;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    margin-block: 1rem;
    padding: 0;
    text-align: center;
    width: 100%;
  }
`;

export default function Card({
  title,
  description,
  imgSrc,
  image,
  imgAlt,
  label,
  linkUrl,
  twoCol,
  isSmall,
  textAlign,
}) {
  return (
    <SmWrapper $twoCol={twoCol} $isSmall={isSmall}>
      {linkUrl ? (
        <>
          <LinkImage to={linkUrl} $twoCol={twoCol} $isSmall={isSmall}>
            <Image
              $disableHover={false}
              src={imgSrc}
              alt={imgAlt}
              image={image}
            />
          </LinkImage>
          <LinkDiv $isSmall={isSmall} to={linkUrl} $textAlign={textAlign}>
            <Title
              $twoCol={twoCol}
              $isSmall={isSmall}
              dangerouslySetInnerHTML={{ __html: title }}
            />
            <Description $twoCol={twoCol}>
              {typeof description === "string" ? parse(description) : null}{" "}
              {!isSmall && !label && (
                <AbsoluteArrow icon={faLongArrowAltRight} size="lg" />
              )}
            </Description>
            {label && (
              <CardLink to={linkUrl}>
                <span dangerouslySetInnerHTML={{ __html: label }} />
                <ArrowIcon icon={faLongArrowAltRight} size="lg" />
              </CardLink>
            )}
          </LinkDiv>
        </>
      ) : (
        <>
          <ImageWrapper $twoCol={twoCol} $isSmall={isSmall}>
            <Image
              $disableHover={true}
              src={imgSrc}
              alt={imgAlt}
              image={image}
            />
          </ImageWrapper>
          <TextDiv $isSmall={isSmall} $textAlign={textAlign}>
            <Title
              $twoCol={twoCol}
              $isSmall={isSmall}
              dangerouslySetInnerHTML={{ __html: title }}
            />
            <Description $twoCol={twoCol}>
              {typeof description === "string" ? parse(description) : null}{" "}
            </Description>
          </TextDiv>
        </>
      )}
    </SmWrapper>
  );
}

Card.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  imgSrc: PropTypes.object,
  image: PropTypes.object,
  imgAlt: PropTypes.string,
  label: PropTypes.string,
  linkUrl: PropTypes.string.isRequired,
  twoCol: PropTypes.bool,
  isSmall: PropTypes.bool,
  textAlign: PropTypes.string,
};
Card.defaultProps = {
  twoCol: false,
  imgSrc: undefined,
  image: undefined,
  imgAlt: undefined,
  label: undefined,
  isSmall: false,
};
